/**
 * DurationPhysics.js
 * Centralized logic for calculating acoustic note durations based on Instrument Physics and Narrative Tension.
 * Replaces hardcoded multipliers and siloed logic in TextureEngine/MelodyStrategies.
 */

export const applyDurationPhysics = (baseDuration, context, intendedArticulation = null) => {
    // Safety Checks
    if (typeof baseDuration !== 'number' || isNaN(baseDuration)) return 0.25;
    if (!context) return baseDuration;

    // Extract Context
    const traits = context.traits || {}; // Instrument Traits
    const role = context.role || 'unknown';
    // Narrative Tension: Can be passed directly or retrieved via function
    let tension = 0.5;
    if (typeof context.tension === 'number') tension = context.tension;
    else if (context.getTension && typeof context.barIndex === 'number') tension = context.getTension(context.barIndex);

    // Determine Articulation (if not explicit)
    let artic = intendedArticulation || context.articulation;
    if (!artic) {
        if (baseDuration < 0.3) artic = 'spiccato';
        else if (baseDuration < 0.8) artic = 'portato';
        else artic = 'legato';
    }

    const isShort = (artic === 'staccato' || artic === 'spiccato' || artic === 'short' || artic === 'hit' || artic === 'kick' || artic === 'snare');

    // --- BEHAVIOR 1: THE CLOUD (Atmospheric Expansion) ---
    // Pads, Textures, and Atmosphere expand with Tension
    const isAtmosphere = role.includes('pad') || role.includes('texture') || role.includes('atmosphere') || role.includes('cloud');
    if (isAtmosphere) {
        // Scale 1x to 4x based on tension
        // Tension 0.0 -> 1x
        // Tension 1.0 -> 4x
        return baseDuration * (1.0 + (tension * 3.0));
    }

    // --- BEHAVIOR 2: THE RING (Resonant Decay) ---
    // Plucked/Struck instruments need time to ring out, even for "short" grid notes.
    // However, if articulation is explicitly short, respect it.
    const instrumentId = (context.instrumentId || '').toLowerCase();
    const isResonant = role.includes('piano') || role.includes('harp') || role.includes('guitar') ||
                       role.includes('pluck') || role.includes('mallet') || role.includes('bell') ||
                       instrumentId.includes('piano') || instrumentId.includes('harp');

    if (isResonant) {
        if (isShort) return baseDuration; // Muted/Staccato
        // "Let it ring": Base + Tail.
        // Tail = 1.2 beats (approx 0.5s - 1.0s).
        return baseDuration + 1.2;
    }

    // --- BEHAVIOR 3: PERCUSSION SUSTAIN (Cymbals) ---
    // High Percussion (Cymbals, Tam-Tam) needs duration for the sample to play out.
    if (role === 'perc_high' || instrumentId.includes('cymbal') || instrumentId.includes('gong') || instrumentId.includes('piatti')) {
        // If it's a hit/crash, force minimum duration
        if (artic === 'hit' || artic === 'crash') {
            return Math.max(baseDuration, 2.0); // Minimum 2 beats
        }
    }

    // --- BEHAVIOR 4: THE GLUE (Legato Overlap) ---
    // Monophonic Sustains: Woodwind, Brass, Strings, Lead
    const isSustainInst = role.includes('melody') || role.includes('counter') ||
                          traits.family === 'woodwinds' || traits.family === 'brass' || traits.family === 'strings' ||
                          role.includes('lead') || role.includes('voice');

    if (isSustainInst) {
         if (isShort) {
             // Spiccato/Staccato Physics: Slightly shorten to ensure separation?
             // Or keep as is. Usually samples handle the decay.
             // Maybe slightly shorter to allow "air".
             return baseDuration * 0.9;
         }

         // Apply Overlap for Legato transitions
         // Scale overlap with tension? High tension = more desperate connection?
         // Standard: 1.15x
         return baseDuration * 1.15;
    }

    // Default: Return original
    return baseDuration;
};

/**
 * Global Post-Processor that applies Duration Physics to a list of events.
 * @param {Array} events - The generated event list.
 * @param {Object} context - Global generation context.
 */
export const applyGlobalPhysics = (events, context) => {
    if (!events || events.length === 0) return events;

    // Iterate and Mutate
    // Using map would be cleaner but mutation is fine for performance if needed.
    // Let's use map to be safe.
    return events.map(ev => {
        // Construct Event Context
        const eventContext = {
            ...context, // Global context (traits might be generic here)
            role: ev.role,
            instrumentId: ev.instrumentId || (ev.instrument ? ev.instrument.id : ''),
            articulation: ev.articulation,
            barIndex: Math.floor(ev.s / 4),
            // If event has specific instrument traits attached (from adaptation), use them
            traits: (ev.instrument && ev.instrument.traits) ? ev.instrument.traits : (context.traits || {})
        };

        const newLen = applyDurationPhysics(ev.len, eventContext, ev.articulation);

        // Return new event with updated length
        return {
            ...ev,
            len: newLen
        };
    });
};
